import {
  InstrumentStatus,
  type PaymentInstrument,
  PaymentModuleId,
} from '@foundation/apis/payments';

// A managed mandate is identified by its reference, so that `id` is unique and usable everywhere.
export function withMandateReferenceAsId<T extends PaymentInstrument>(
  instrument: T,
): T {
  return instrument.mandateReference === undefined
    ? instrument
    : { ...instrument, id: instrument.mandateReference };
}

export function isCard(module: PaymentModuleId): boolean {
  return (
    module === PaymentModuleId.BraintreeCard ||
    module === PaymentModuleId.WorldpayCard ||
    module === PaymentModuleId.WorldpayJCB ||
    module === PaymentModuleId.FlutterwaveCreditCard ||
    module === PaymentModuleId.NuveiCreditCard
  );
}

export function isPayPal(module: PaymentModuleId): boolean {
  return module === PaymentModuleId.BraintreePayPal;
}

export function isPostFinance(module: PaymentModuleId): boolean {
  return module === PaymentModuleId.DirectDebitPostFinance;
}

export function isMpmSepa(module: PaymentModuleId): boolean {
  return module === PaymentModuleId.Sepa;
}

export function isDirectDebitLsv(module: PaymentModuleId): boolean {
  return module === PaymentModuleId.DirectDebitLSV;
}

export function isSepa(module: PaymentModuleId): boolean {
  return isMpmSepa(module) || isBraintreeSepa(module);
}

export function isDirectDebit(module: PaymentModuleId): boolean {
  return (
    isMpmSepa(module) ||
    isBraintreeSepa(module) ||
    isDirectDebitLsv(module) ||
    isPostFinance(module)
  );
}

export function isBraintreeSepa(module: PaymentModuleId): boolean {
  return module === PaymentModuleId.BraintreeSepa;
}

export function isGooglePay(module: PaymentModuleId): boolean {
  return module === PaymentModuleId.BraintreeGooglePay;
}

export function determineMethodPreSelection(
  preselectedPaymentMethod: PaymentModuleId | undefined,
  allowedMethods: ReadonlyArray<PaymentModuleId>,
): PaymentModuleId | undefined {
  if (
    preselectedPaymentMethod &&
    allowedMethods.includes(preselectedPaymentMethod)
  ) {
    return preselectedPaymentMethod;
  } else {
    return allowedMethods[0];
  }
}

export function determineInstrumentPreSelection(
  preselectedPaymentMethod: PaymentModuleId | undefined,
  instruments: ReadonlyArray<PaymentInstrument>,
): PaymentInstrument | undefined {
  const candidate = instruments
    // Find instruments for the preselected value in the intent
    .filter((i) => i.moduleId === preselectedPaymentMethod)
    // Ensure non Valid instruments are not selected
    .filter((i) => i.status === InstrumentStatus.Valid)
    .shift();

  if (!candidate) {
    // If there's no candidate, find the first valid instrument or undefined
    return instruments.filter((i) => i.status === InstrumentStatus.Valid)[0];
  }

  return candidate;
}
